import { computed, reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import type { ActionResult, ListQuery, PageDefinition, SelectOption, SummaryDef } from '@/types/page'
import type { AdminRecord } from '@/types/record'
import type { SeedDatabase } from '@/data/seed'
import { database } from '@/data/adapters/memory'
import { adapter } from '@/services/prototype-adapter'
import { nowIso } from '@/utils/format'
import { requestNo } from '@/utils/id'
import { useAuthStore } from '@/stores/auth'
import { toPlain } from '@/utils/clone'
import { errorMessage } from '@/utils/errors'

const DATE_FIELDS = new Set([
  'created_at',
  'updated_at',
  'registered_at',
  'last_active_at',
  'scheduled_at',
  'measured_at',
  'triggered_at',
  'generated_at',
  'sent_at',
  'uploaded_at',
  'last_report_at',
  'last_login_at',
  'joined_at',
  'published_at',
])

const PAGE_ID_FIELDS: Record<string, string> = {
  users: 'user',
  families: 'family',
  regions: 'region',
  'medication-plans': 'plan',
  'medication-records': 'record',
  'health-monitoring': 'metric',
  'health-alerts': 'alert',
  'alert-rules': 'rule',
  'health-reports': 'report',
  'care-team': 'care',
  devices: 'device',
  drugs: 'drug',
  'drug-review': 'drug',
  messages: 'message',
  'notification-templates': 'template',
  'content-tips': 'tip',
  'content-faq': 'faq',
  'membership-plans': 'mplan',
  'membership-orders': 'order',
  links: 'link',
  assets: 'asset',
  'support-tickets': 'ticket',
  'support-feedback': 'feedback',
  'admin-users': 'admin',
  roles: 'role',
  'audit-logs': 'audit',
}

const NUMBER_FIELDS: Record<string, string> = {
  users: 'user_no',
  families: 'family_no',
  'medication-plans': 'plan_no',
  'health-alerts': 'alert_no',
  'health-reports': 'report_no',
  devices: 'device_no',
  drugs: 'drug_no',
  messages: 'message_no',
  'membership-plans': 'plan_no',
  'membership-orders': 'order_no',
  'support-tickets': 'ticket_no',
  'support-feedback': 'feedback_no',
  'admin-users': 'admin_no',
}

const NUMBER_PREFIXES: Record<string, string> = {
  users: 'KH',
  families: 'JT',
  'medication-plans': 'YY',
  'health-alerts': 'YJ',
  'health-reports': 'BG',
  devices: 'SB',
  drugs: 'YP',
  messages: 'XX',
  'membership-plans': 'HY',
  'membership-orders': 'DD',
  'support-tickets': 'GD',
  'support-feedback': 'FK',
  'admin-users': 'GL',
}

interface PrototypeState {
  database: SeedDatabase
  revision: number
  lastMutation: string
  settings: {
    platformName: string
    descriptor: string
    servicePhone: string
    serviceHours: string
    serviceEmail: string
    sessionTimeout: number
    passwordMinLength: number
    requireMfa: boolean
    exportLimit: number
    defaultChannels: string[]
    alertSlaMinutes: number
    maintenanceMode: boolean
    maintenanceNotice: string
    prototypeDevMode: boolean
  }
}

export interface QueryResult {
  rows: AdminRecord[]
  total: number
  page: number
  pageSize: number
  summary: Record<string, number | string>
}

export const SETTINGS_VERSION = '1.0.0'
export const PROTOTYPE_REFERENCE_FILES = [
  'reference-1.png',
  'reference-2.png',
  'reference-3.png',
  'reference-4.png',
  'reference-5.png',
]

function bucketFor(db: SeedDatabase, page: PageDefinition): AdminRecord[] {
  const key = page.seedKey as keyof SeedDatabase | undefined
  const value = key ? db[key] : undefined
  return Array.isArray(value) ? (value as AdminRecord[]) : []
}

function normalize(value: unknown): string {
  return String(value ?? '').toLowerCase().trim()
}

function plainValues(value: unknown): string {
  if (Array.isArray(value)) return value.map(item => normalize(item)).join(' ')
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return ''
  return normalize(value)
}

function dateValue(row: AdminRecord, field: string | undefined): number | null {
  if (!field) return null
  const raw = row[field]
  if (typeof raw !== 'string' || !raw) return null
  const date = new Date(raw.length === 10 ? `${raw}T00:00:00+08:00` : raw)
  return Number.isNaN(date.getTime()) ? null : date.getTime()
}

function inDateRange(row: AdminRecord, field: string | undefined, range: unknown): boolean {
  if (!Array.isArray(range) || range.length !== 2) return true
  const value = dateValue(row, field)
  if (value === null) return false
  const start = range[0] ? new Date(`${String(range[0])}T00:00:00+08:00`).getTime() : null
  const end = range[1] ? new Date(`${String(range[1])}T23:59:59+08:00`).getTime() : null
  if (start !== null && value < start) return false
  if (end !== null && value > end) return false
  return true
}

function matchFilter(
  row: AdminRecord,
  page: PageDefinition,
  key: string,
  value: unknown,
): boolean {
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return true
  }
  const filter = page.filters.find(item => item.key === key)
  if (filter?.type === 'daterange') {
    return inDateRange(row, filter.timeField ?? page.timeField, value)
  }
  if (filter?.type === 'multiselect' && Array.isArray(value)) {
    return value.some(item => matchValue(row, key, item, filter.options))
  }
  if (Array.isArray(value)) return value.includes(row[key] as string)
  return matchValue(row, key, value, filter?.options)
}

function matchValue(
  row: AdminRecord,
  key: string,
  value: unknown,
  options?: SelectOption[],
): boolean {
  if (key === 'bound') {
    const bound = row.status !== 'unbound'
    return String(bound) === String(value)
  }
  if (value === 'true' || value === 'false') {
    const actual = row[key]
    if (typeof actual === 'boolean') return String(actual) === String(value)
  }
  const actualText = normalize(row[key])
  const expectedText = normalize(value)
  if (actualText === expectedText) return true
  if (Array.isArray(row[key])) return (row[key] as unknown[]).some(item => normalize(item) === expectedText)
  if (options) {
    const optionLabel = options.find(option => String(option.value) === String(value))?.label
    if (optionLabel && normalize(optionLabel) === actualText) return true
    const normalizedLabel = optionLabel?.replace(/监测$/, '')
    if (normalizedLabel && normalize(normalizedLabel) === actualText) return true
  }
  return false
}

function matchesWhere(row: AdminRecord, where: Record<string, string | boolean | number> = {}): boolean {
  return Object.entries(where).every(([key, expected]) => {
    if (key === 'age_gte') return Number(row.age ?? 0) >= Number(expected)
    if (key === 'battery_lte') return Number(row.battery ?? 100) <= Number(expected)
    if (key === 'stock_low') return Number(row.stock ?? 0) < Number(row.safety_stock ?? 0)
    if (key === 'registered_within_days' || key === 'created_within_days') {
      const field = key === 'registered_within_days' ? 'registered_at' : 'created_at'
      const time = dateValue(row, field)
      if (time === null) return false
      return time >= Date.now() - Number(expected) * 86_400_000
    }
    if (key === 'device_bound') return Boolean(row.device_bound) === Boolean(expected)
    return row[key] === expected
  })
}

function computeSummary(rows: AdminRecord[], definition: SummaryDef): number | string {
  const base = rows.filter(row => matchesWhere(row, definition.where))
  if (definition.kind === 'count') return base.length
  if (definition.kind === 'sum') {
    return base.reduce((total, row) => total + Number(row[definition.field ?? ''] ?? 0), 0)
  }
  if (definition.kind === 'distinct') {
    return new Set(base.map(row => String(row[definition.field ?? ''] ?? '')).filter(Boolean)).size
  }
  if (definition.kind === 'ratio') {
    if (definition.field && base.some(row => Number.isFinite(Number(row[definition.field ?? ''])))) {
      const values = base.map(row => Number(row[definition.field ?? ''] ?? 0)).filter(Number.isFinite)
      return values.length ? Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1)) : 0
    }
    const denominatorRows = definition.denominatorWhere ? rows.filter(row => matchesWhere(row, definition.denominatorWhere)) : rows
    return denominatorRows.length ? Number(((base.length / denominatorRows.length) * 100).toFixed(1)) : 0
  }
  return 0
}

export function queryRecords(page: PageDefinition, rows: AdminRecord[], query: Partial<ListQuery>): QueryResult {
  const keyword = normalize(query.keyword)
  const filters = query.filters ?? {}
  const filtered = rows.filter(row => {
    if (keyword && !page.searchKeys.some(key => plainValues(row[key]).includes(keyword))) return false
    if (query.status && page.statusKey && String(row[page.statusKey] ?? '') !== query.status) return false
    for (const [key, value] of Object.entries(filters)) {
      if (!matchFilter(row, page, key, value)) return false
    }
    if (query.dateStart || query.dateEnd) {
      if (!inDateRange(row, page.timeField, [query.dateStart, query.dateEnd])) return false
    }
    return true
  })

  const sortField = query.sortField
  const sorted = [...filtered]
  if (sortField) {
    const direction = query.sortOrder === 'desc' ? -1 : 1
    sorted.sort((left, right) => {
      const a = left[sortField]
      const b = right[sortField]
      const aTime = dateValue(left, sortField)
      const bTime = dateValue(right, sortField)
      if (aTime !== null && bTime !== null) return (aTime - bTime) * direction
      if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction
      return String(a ?? '').localeCompare(String(b ?? ''), 'zh-CN') * direction
    })
  }

  const pageSize = Math.max(1, Number(query.pageSize) || 10)
  const currentPage = Math.max(1, Number(query.page) || 1)
  const total = sorted.length
  const maxPage = Math.max(1, Math.ceil(total / pageSize))
  const normalizedPage = Math.min(currentPage, maxPage)
  const paged = sorted.slice((normalizedPage - 1) * pageSize, normalizedPage * pageSize)
  const summary = Object.fromEntries((page.summary ?? []).map(item => [item.key, computeSummary(rows, item)]))
  return { rows: paged, total, page: normalizedPage, pageSize, summary }
}

function nextBusinessNo(rows: AdminRecord[], page: PageDefinition, field: string): string {
  const prefix = NUMBER_PREFIXES[page.id] ?? 'P'
  const numbers = rows
    .map(row => String(row[field] ?? ''))
    .map(value => Number(value.replace(/\D/g, '')))
    .filter(Number.isFinite)
  const next = (numbers.length ? Math.max(...numbers) : 0) + 1
  return `${prefix}${String(next).padStart(6, '0')}`
}

function findById(rows: AdminRecord[], id: unknown): AdminRecord | undefined {
  return rows.find(row => String(row.id) === String(id ?? ''))
}

function deriveRow(page: PageDefinition, row: AdminRecord, db: SeedDatabase): AdminRecord {
  const now = nowIso()
  row.updated_at = now
  row.row_version = Number(row.row_version ?? 0) + 1

  if (page.id === 'users') {
    const birth = String(row.birth_date ?? '')
    if (birth) row.age = Math.max(0, new Date().getFullYear() - Number(birth.slice(0, 4)))
    const family = findById(db.families, row.family_id)
    if (family) {
      row.family_name = family.name
      row.region_id = row.region_id || family.region_id
    }
    const region = findById(db.regions, row.region_id)
    if (region) row.region_name = region.name
  }
  if (page.id === 'families') {
    const owner = findById(db.users, row.owner_user_id)
    if (owner) {
      row.owner_name = owner.name
      row.address = row.address || owner.address
    }
    const region = findById(db.regions, row.region_id)
    if (region) row.region_name = region.name
    row.region_path = region?.path ?? row.region_path
    row.member_count = Math.max(1, Number(row.member_count ?? 1))
  }
  if (page.id === 'regions') {
    const parent = findById(db.regions, row.parent_id)
    row.parent_name = parent?.name ?? '—'
    row.path = parent?.path ? `${parent.path} / ${String(row.name ?? '')}` : String(row.name ?? '')
    row.level_label = row.level === 'province' ? '省级' : row.level === 'city' ? '市级' : '区县级'
  }
  if (page.id === 'medication-plans') {
    const user = findById(db.users, row.user_id)
    const family = findById(db.families, row.family_id)
    const drug = findById(db.drugs, row.drug_id)
    const owner = findById(db.care_team, row.owner_id)
    if (user) row.user_name = user.name
    if (family) row.family_name = family.name
    if (drug) row.drug_name = drug.generic_name
    if (owner) row.owner_name = owner.name
  }
  if (page.id === 'medication-records') {
    row.recorded_by = row.recorded_by || '后台更正'
    row.note = row.note || ''
  }
  if (page.id === 'devices') {
    const user = findById(db.users, row.user_id)
    const family = findById(db.families, row.family_id)
    const region = findById(db.regions, row.region_id)
    if (user) {
      row.user_name = user.name
      row.family_id = row.family_id || user.family_id
      row.family_name = family?.name ?? user.family_name
    }
    if (family) row.family_name = family.name
    if (region) row.region_name = region.name
    if (!row.user_id) {
      row.user_name = ''
      row.family_name = ''
      row.status = 'unbound'
    }
  }
  if (page.id === 'drugs' || page.id === 'drug-review') {
    row.category_name = row.category_name || '其他'
    row.updated_at = now
  }
  if (page.id === 'health-alerts') {
    const owner = findById(db.care_team, row.owner_id)
    if (owner) row.owner_name = owner.name
  }
  if (page.id === 'health-reports') {
    const user = findById(db.users, row.user_id)
    if (user) {
      row.user_name = user.name
      row.family_name = user.family_name
    }
  }
  if (page.id === 'content-tips') {
    row.cover_color = row.cover_color || '#DDF3EA'
  }
  if (page.id === 'admin-users') {
    const roleCodes = Array.isArray(row.role_codes) ? row.role_codes : []
    const roleNames: Record<string, string> = {
      super_admin: '超级管理员',
      operations_manager: '运营管理员',
      health_advisor: '健康顾问',
      drug_librarian: '药品库管理员',
      drug_reviewer: '药事审核员',
      support_agent: '客服专员',
      device_ops: '设备运维',
      analyst: '数据分析员',
      auditor: '审计员',
    }
    row.role_names = roleCodes.map(code => roleNames[String(code)] ?? String(code))
  }
  return row
}

function initialDefaults(page: PageDefinition, db: SeedDatabase): AdminRecord {
  const now = nowIso()
  const row: AdminRecord = {
    id: `${PAGE_ID_FIELDS[page.id] ?? 'record'}-${Date.now()}`,
    created_at: now,
    updated_at: now,
    row_version: 1,
  }
  const numberField = NUMBER_FIELDS[page.id]
  if (numberField) row[numberField] = nextBusinessNo(bucketFor(db, page), page, numberField)
  if (page.id === 'users') {
    row.status = 'active'
    row.risk_level = '低风险'
    row.tags = []
    row.device_bound = false
    row.consent_scope = ['健康数据共享']
  }
  if (page.id === 'families') {
    row.status = 'active'
    row.risk_level = '低风险'
    row.member_count = 1
    row.guardian_count = 1
  }
  if (page.id === 'regions') row.status = 'active'
  if (page.id === 'medication-plans') {
    row.status = 'active'
    row.adherence = 100
    row.timing = []
  }
  if (page.id === 'devices') {
    row.status = 'unbound'
    row.battery = 100
  }
  if (page.id === 'drugs') {
    row.review_status = 'pending'
    row.otc_type = '处方药'
    row.stock = 0
    row.safety_stock = 0
  }
  if (page.id === 'health-alerts') row.status = 'pending'
  if (page.id === 'alert-rules') {
    row.status = 'active'
    row.version = 1
  }
  if (page.id === 'content-tips') row.status = 'online'
  if (page.id === 'content-faq') row.status = 'published'
  if (page.id === 'membership-plans') row.status = 'online'
  if (page.id === 'links') {
    row.status = 'active'
    row.usage_count = 0
  }
  if (page.id === 'assets') {
    row.status = 'active'
    row.usage_count = 0
  }
  if (page.id === 'support-tickets') row.status = 'pending'
  if (page.id === 'admin-users') {
    row.status = 'active'
    row.mfa_enabled = true
    row.role_codes = []
    row.role_names = []
  }
  if (page.id === 'roles') {
    row.status = 'active'
    row.is_builtin = false
  }
  return row
}

export const usePrototypeStore = defineStore('prototype', () => {
  const state = reactive<PrototypeState>({
    database: database as SeedDatabase,
    revision: 0,
    lastMutation: '',
    settings: {
      platformName: '康护健康',
      descriptor: '运营工作台',
      servicePhone: '400-800-2026',
      serviceHours: '每日 08:00-20:00',
      serviceEmail: 'service@kanghu.example',
      sessionTimeout: 30,
      passwordMinLength: 8,
      requireMfa: true,
      exportLimit: 5000,
      defaultChannels: ['站内信'],
      alertSlaMinutes: 30,
      maintenanceMode: false,
      maintenanceNotice: '系统维护中，健康数据上报与后台管理将短暂停止。',
      prototypeDevMode: false,
    },
  })

  const lastResetAt = ref(nowIso())

  const db = state.database

  function bucket(page: PageDefinition): AdminRecord[] {
    return bucketFor(db, page)
  }

  function options(_page: PageDefinition, source: PageDefinition['filters'][number]['optionSource']): SelectOption[] {
    return adapter.options(String(source ?? ''))
  }

  function query(page: PageDefinition, input: Partial<ListQuery>): QueryResult {
    // Central prototype adapter executes keyword/status/time-range filtering,
    // natural sorting and server-style pagination against the reactive memory
    // database. Query payloads are plain JSON before crossing this boundary.
    const plainInput = toPlain(input) as ListQuery
    const result = adapter.list(page, plainInput)
    return {
      rows: result.items,
      total: result.total,
      page: result.page,
      pageSize: result.pageSize,
      summary: result.summary,
    }
  }

  function failure(error: unknown): ActionResult {
    return {
      ok: false,
      message: errorMessage(error),
      fieldErrors: error instanceof Error && 'fieldErrors' in error ? (error as { fieldErrors?: Record<string, string> }).fieldErrors : undefined,
      conflict: error instanceof Error && 'details' in error
        ? ((error as { details?: { conflict?: ActionResult['conflict'] } }).details?.conflict)
        : undefined,
    }
  }

  function getRow(page: PageDefinition, id: unknown): AdminRecord | undefined {
    return bucket(page).find(row => String(row.id) === String(id))
  }

  function addAudit(page: PageDefinition, action: string, resourceLabel: string, result: '成功' | '失败' = '成功', detail = ''): void {
    const auth = useAuthStore()
    const actor = auth.profile?.name ?? '系统'
    const account = auth.profile?.account ?? 'system'
    const now = nowIso()
    db.audit_logs.unshift({
      id: `audit-${Date.now()}-${Math.floor(Math.random() * 999)}`,
      created_at: now,
      actor_name: actor,
      actor_account: account,
      module: page.group,
      action,
      resource_type: page.title,
      resource_label: resourceLabel,
      result,
      ip: '127.0.0.1',
      request_no: requestNo(),
      before_json: '',
      after_json: '',
      detail: detail || '原型内存中的交互已记录，未写入数据库',
      row_version: 1,
    })
  }

  function createRecord(page: PageDefinition, values: Record<string, unknown>): ActionResult {
    if (page.readOnly) return { ok: false, message: '当前页面为只读，不能新增' }
    const auth = useAuthStore()
    try {
      const record = adapter.create(page, toPlain(values), auth.profile?.name ?? '管理员')
      state.revision += 1
      state.lastMutation = `新增${page.title}`
      return { ok: true, message: `已新增 1 条${page.title}记录`, item: record }
    } catch (error) {
      return failure(error)
    }
  }

  function updateRecord(page: PageDefinition, id: unknown, values: Record<string, unknown>): ActionResult {
    const rows = bucket(page)
    const index = rows.findIndex(row => String(row.id) === String(id))
    if (index < 0) return { ok: false, message: '记录不存在或已被删除' }
    if (page.readOnly) return { ok: false, message: '当前页面为只读，不能编辑' }
    const current = rows[index] as AdminRecord
    const auth = useAuthStore()
    try {
      const payload = toPlain(values)
      const expected = Number(payload.__rowVersion ?? values.__rowVersion ?? payload.row_version ?? current.row_version ?? 1)
      payload.__rowVersion = expected
      const record = adapter.update(page, String(id), payload, auth.profile?.name ?? '管理员')
      state.revision += 1
      state.lastMutation = `编辑${page.title}`
      return { ok: true, message: '修改已保存', item: record }
    } catch (error) {
      return failure(error)
    }
  }

  function transition(
    page: PageDefinition,
    ids: unknown[],
    field: string,
    value: unknown,
    reason = '',
    statusLabel = '',
  ): ActionResult {
    const rows = bucket(page)
    let succeeded = 0
    for (const id of ids) {
      const row = rows.find(item => String(item.id) === String(id))
      if (!row) continue
      row[field] = value
      if (reason) row.close_reason = reason
      row.updated_at = nowIso()
      row.row_version = Number(row.row_version ?? 1) + 1
      succeeded += 1
    }
    adapter.refreshDerived()
    state.revision += 1
    state.lastMutation = `${statusLabel || String(value)}${page.title}`
    if (succeeded) addAudit(page, statusLabel || String(value), `${succeeded} 条${page.title}`)
    return {
      ok: succeeded > 0,
      message: succeeded ? `已将 ${succeeded} 条记录更新为${statusLabel || String(value)}` : '没有可更新的记录',
      succeeded,
    }
  }

  function removeRecords(page: PageDefinition, ids: unknown[]): ActionResult {
    if (page.readOnly) return { ok: false, message: '当前页面为只读，不能删除' }
    const auth = useAuthStore()
    let succeeded = 0
    const failed: Array<{ id: string; label: string; message: string }> = []
    let firstConflict: ActionResult['conflict']
    for (const id of ids) {
      const row = bucket(page).find(item => String(item.id) === String(id))
      try {
        adapter.remove(page, String(id), auth.profile?.name ?? '管理员')
        succeeded += 1
      } catch (error) {
        const result = failure(error)
        if (result.conflict && !firstConflict) firstConflict = result.conflict
        failed.push({ id: String(id), label: String(row?.name ?? row?.title ?? id), message: result.message })
      }
    }
    state.revision += 1
    state.lastMutation = `删除${page.title}`
    if (succeeded) addAudit(page, '删除', `${succeeded} 条${page.title}`)
    const message = succeeded && failed.length
      ? `已删除 ${succeeded} 条，${failed.length} 条未删除`
      : succeeded
        ? `已删除 ${succeeded} 条记录`
        : failed.length
          ? failed[0]!.message
          : '没有可删除的记录'
    return { ok: succeeded > 0, message, succeeded, failed, conflict: firstConflict }
  }

  function reset(): void {
    adapter.reset()
    state.revision += 1
    lastResetAt.value = nowIso()
    state.lastMutation = '重置原型内存数据'
    state.settings.prototypeDevMode = false
    state.settings.maintenanceMode = false
  }

  function setDevMode(enabled: boolean): void {
    state.settings.prototypeDevMode = enabled
  }

  const badges = computed(() => ({
    pendingReminders: db.medication_plans.filter(row => row.status === 'active' || row.status === 'overdue').length,
    openAlerts: db.alerts.filter(row => row.status === 'pending' || row.status === 'processing').length,
    offlineDevices: db.devices.filter(row => row.status === 'offline' || row.status === 'fault').length,
    pendingReviews: db.drugs.filter(row => row.review_status === 'pending').length,
    openTickets: db.tickets.filter(row => !['resolved', 'closed'].includes(String(row.status))).length,
    pendingFeedback: db.feedback.filter(row => !['replied', 'closed'].includes(String(row.status))).length,
  }))

  return {
    state,
    db,
    revision: computed(() => state.revision),
    lastResetAt,
    settings: computed(() => state.settings),
    badges,
    bucket,
    query,
    getRow,
    options,
    createRecord,
    updateRecord,
    transition,
    removeRecords,
    reset,
    setDevMode,
    addAudit,
  }
})

